import { useState, useCallback, useEffect, useRef, forwardRef } from "react";
import { captureException } from "@/lib/sentry";
import { buildImageSrcSet } from "@shared/image-srcset";

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  sizes?: string;
  priority?: boolean;
  aspectRatio?: string;
}

export const OptimizedImage = forwardRef<HTMLImageElement, OptimizedImageProps>(
  ({ src, alt, sizes, priority = false, aspectRatio, className, style, onLoad, srcSet: propSrcSet, ...rest }, ref) => {
    // Priority images (LCP candidates, above-the-fold) are preloaded and must be
    // visible immediately — starting them at opacity 0 would delay Lighthouse's
    // LCP measurement by one React render cycle + 400 ms of CSS transition.
    // Non-priority images fade in after loading to avoid layout shift on scroll.
    const [visible, setVisible] = useState(priority);
    const imgRef = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
      // Non-priority images: reset to invisible when src changes so the new
      // image fades in cleanly. Priority images stay visible at all times.
      if (!priority) {
        setVisible(false);
        const timer = setTimeout(() => setVisible(true), 3000);
        return () => clearTimeout(timer);
      }
    }, [src, priority]);

    useEffect(() => {
      if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
        setVisible(true);
      }
    });

    const setRefs = useCallback(
      (node: HTMLImageElement | null) => {
        imgRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLImageElement | null>).current = node;
        if (node?.complete && node.naturalWidth > 0) {
          setVisible(true);
        }
      },
      [ref],
    );

    const handleLoad = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        setVisible(true);
        onLoad?.(e);
      },
      [onLoad],
    );

    const handleError = useCallback(() => {
      setVisible(true);
      captureException(new Error(`Image failed to load: ${src}`), { src, alt });
    }, [src, alt]);

    const aspectStyle = aspectRatio
      ? { aspectRatio: aspectRatio === "square" ? "1/1" : aspectRatio }
      : undefined;

    const derivedSrcSet = propSrcSet ?? buildImageSrcSet(src);

    return (
      <img
        ref={setRefs}
        src={src}
        srcSet={derivedSrcSet}
        alt={alt}
        sizes={derivedSrcSet ? (sizes ?? "100vw") : undefined}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? undefined : "async"}
        fetchPriority={priority ? "high" : undefined}
        onLoad={handleLoad}
        onError={handleError}
        className={className}
        style={{
          backgroundColor: "#000",
          opacity: visible ? 1 : 0.01,
          // Skip the fade-in transition for priority (LCP) images — they should
          // appear instantly. Non-priority images fade in smoothly.
          transition: priority ? undefined : "opacity 0.4s ease-in-out",
          ...aspectStyle,
          ...style,
        }}
        {...rest}
      />
    );
  },
);

OptimizedImage.displayName = "OptimizedImage";
